import React from 'react';
import { Alert, Button, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertCircle, IconCircleCheck, IconMailCheck } from '@tabler/icons-react';
import { Navigate, useNavigate } from 'react-router-dom';
import AuthFrame from '@/components/auth/frame/Auth.Frame';
import SessionSplash from '@/router/Session.Splash';
import { emailChangeArrival } from '@/lib/email-change/email.change.arrival';
import { sessionLoad } from '@/lib/session/session.bootstrap';
import { useConfirmAccountEmailMutation } from '@/store/api/auth/account.api';
import { selectSessionStatus, selectSessionUser } from '@/store/auth/auth.selectors';
import { useAppDispatch, useAppSelector } from '@/store/store.hooks';
import type { TypeApiError } from '@/types/api/types.api';
import { EMAIL_CHANGE_PATH } from '@/config/email.change.config';

type Outcome = { kind: 'idle' } | { kind: 'done'; email: string } | { kind: 'failed'; message: string; final: boolean };

const FINAL = new Set([400, 404, 409]);

const EmailChange: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const status = useAppSelector(selectSessionStatus);
    const user = useAppSelector(selectSessionUser);
    const [token] = React.useState(() => emailChangeArrival.peek());
    const [confirm, { isLoading }] = useConfirmAccountEmailMutation();
    const [outcome, setOutcome] = React.useState<Outcome>({ kind: 'idle' });

    const leave = () => {
        emailChangeArrival.take();
        navigate('/profile/account', { replace: true });
    };

    if (status === 'unknown') return <SessionSplash />;
    if (status === 'anonymous') return <Navigate to={`/auth/login?next=${encodeURIComponent(EMAIL_CHANGE_PATH)}`} replace />;

    const run = async () => {
        if (token === null || isLoading) return;
        try {
            const result = await confirm({ token }).unwrap();
            emailChangeArrival.take();
            await sessionLoad(dispatch);
            setOutcome({ kind: 'done', email: result.email });
        } catch (error) {
            const rejection = error as TypeApiError;
            setOutcome({ kind: 'failed', message: rejection?.message ?? 'La dashboard non risponde: riprova fra poco.', final: FINAL.has(rejection?.status ?? 0) });
        }
    };

    if (token === null) {
        return (
            <AuthFrame title="Link non valido" subtitle="Il link per cambiare l’email non è completo.">
                <Stack align="center" gap="md">
                    <ThemeIcon size={56} radius="xl" variant="light" color="danger"><IconAlertCircle size={32} /></ThemeIcon>
                    <Text size="sm" c="dimmed" ta="center">Aprilo di nuovo dall’email, oppure chiedi all’amministratore di rifare il cambio.</Text>
                    <Button fullWidth mih={44} onClick={leave}>Torna al profilo</Button>
                </Stack>
            </AuthFrame>
        );
    }

    if (outcome.kind === 'done') {
        return (
            <AuthFrame title="Email cambiata" subtitle={outcome.email}>
                <Stack align="center" gap="md" role="status">
                    <ThemeIcon size={56} radius="xl" variant="light" color="success"><IconCircleCheck size={32} /></ThemeIcon>
                    <Text size="sm" c="dimmed" ta="center">Da ora entri con <b>{outcome.email}</b>. Le altre sessioni sono state chiuse per sicurezza.</Text>
                    <Button fullWidth mih={44} onClick={leave}>Vai al profilo</Button>
                </Stack>
            </AuthFrame>
        );
    }

    return (
        <AuthFrame title="Conferma la nuova email" subtitle={user?.email ? `Account di ${user.email}` : 'Il tuo account'}>
            <Stack gap="md">
                <Stack align="center" gap="xs">
                    <ThemeIcon size={56} radius="xl" variant="light" color="brand"><IconMailCheck size={32} /></ThemeIcon>
                    <Text size="sm" c="dimmed" ta="center">L’amministratore ha proposto una nuova email di accesso per il tuo account. Confermala solo se l’hai chiesta tu: per sicurezza ti chiediamo di confermare la tua identità.</Text>
                </Stack>
                {outcome.kind === 'failed' ? <Alert color="danger" variant="light" icon={<IconAlertCircle size={16} />}><Text size="sm">{outcome.message}</Text></Alert> : null}
                <Button fullWidth mih={44} loading={isLoading} disabled={isLoading || (outcome.kind === 'failed' && outcome.final)} onClick={() => void run()}>Conferma</Button>
                <Button fullWidth mih={44} variant="subtle" color="gray" disabled={isLoading} onClick={leave}>Non ora</Button>
            </Stack>
        </AuthFrame>
    );
};

export default EmailChange;
